import { QRCodeSVG } from 'qrcode.react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';

const Demo2 = () => {
  const [zoomedPage, setZoomedPage] = useState<'left' | 'right' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const PageContent = ({ side }: { side: 'left' | 'right' }) => {
    const isZoomed = zoomedPage === side;
    const baseClasses = "bg-white shadow-lg border-gray-200 transition-all duration-300";
    const sizeClasses = isZoomed 
      ? "fixed top-0 left-1/2 -translate-x-1/2 z-50" 
      : `w-[300px] h-[400px] ${side === 'left' ? 'rounded-l-lg rotate-y-1 border-r' : 'rounded-r-lg rotate-y-[-1deg] border-l'}`;

    const pageContent = (
      <div className={`w-[300px] h-[400px] ${isZoomed ? 'transform scale-[2.0] origin-top bg-white rounded-lg shadow-2xl' : ''} relative`}>
        <button
          onClick={() => setZoomedPage(isZoomed ? null : side)}
          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors z-50"
        >
          {isZoomed ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
        <div className="p-6 h-full">
          <div className="text-[6px] leading-tight space-y-2">
            {side === 'left' ? (
              <>
                <h2 className="text-[8px] font-bold mb-2">Die Kunst der Nachtfotografie</h2>
                <p>
                  Die Nachtfotografie eröffnet eine faszinierende Welt voller magischer Momente und einzigartiger Bildkompositionen. In der Dunkelheit entstehen besondere Atmosphären, die tagsüber verborgen bleiben. Mit den richtigen Techniken und etwas Übung können Sie beeindruckende Nachtaufnahmen erstellen.
                </p>
                <img
                  src="https://images.unsplash.com/photo-1519681393784-d120267933ba?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                  alt="Nachtfotografie"
                  className="w-full h-24 object-cover rounded-lg my-2"
                />
                <p className="text-center text-[5px] text-gray-600 mb-2">Sternenhimmel über Berglandschaft</p>
                <h3 className="text-[7px] font-semibold">Grundlegende Ausrüstung</h3>
                <ul className="list-disc pl-3 space-y-1">
                  <li>Statives Stativ für lange Belichtungszeiten</li>
                  <li>Kamera mit manuellen Einstellungsmöglichkeiten</li>
                  <li>Weitwinkelobjektiv (14-35mm)</li>
                  <li>Fernauslöser oder Selbstauslöser</li>
                  <li>Ersatzakkus und Speicherkarten</li>
                </ul>
                <h3 className="text-[7px] font-semibold mt-2">Vorbereitung</h3>
                <p>
                  Die sorgfältige Planung ist bei der Nachtfotografie besonders wichtig. Informieren Sie sich über:
                </p>
                <ul className="list-disc pl-3 space-y-1">
                  <li>Wettervorhersage und Bewölkung</li>
                  <li>Mondphase und Mondaufgang</li>
                  <li>Lichtverschmutzung am Aufnahmeort</li>
                  <li>Zugänglichkeit und Sicherheit</li>
                </ul>
              </>
            ) : (
              <>
                <h3 className="text-[7px] font-semibold">Kameraeinstellungen</h3>
                <p>
                  Die optimalen Kameraeinstellungen variieren je nach Motiv und Lichtsituation. Hier sind grundlegende Richtwerte:
                </p>
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <h4 className="text-[6px] font-semibold mb-1">Sternenhimmel:</h4>
                  <ul className="text-[5px] list-disc pl-3 space-y-1">
                    <li>ISO: 1600-3200</li>
                    <li>Blende: f/2.8-f/4</li>
                    <li>Belichtungszeit: 15-30 Sekunden</li>
                  </ul>
                </div>
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <h4 className="text-[6px] font-semibold mb-1">Stadtlichter:</h4>
                  <ul className="text-[5px] list-disc pl-3 space-y-1">
                    <li>ISO: 100-400</li>
                    <li>Blende: f/8-f/11</li>
                    <li>Belichtungszeit: 1-5 Sekunden</li>
                  </ul>
                </div>
                <h3 className="text-[7px] font-semibold mt-2">Fokussierung</h3>
                <p>
                  Die korrekte Fokussierung ist nachts besonders herausfordernd. Verwenden Sie:
                </p>
                <ul className="list-disc pl-3 space-y-1">
                  <li>Manuellen Fokus</li>
                  <li>Live-View mit 10x Vergrößerung</li>
                  <li>Helle Sterne oder entfernte Lichter</li>
                </ul>
                <h3 className="text-[7px] font-semibold mt-2">Bildkomposition</h3>
                <p>
                  Achten Sie auf interessante Vordergrund-elemente, die Ihren Aufnahmen Tiefe verleihen. Nutzen Sie natürliche Führungslinien und den Goldenen Schnitt.
                </p>
                <div className="mt-2">
                  <QRCodeSVG
                    value="https://night-city-stories.replit.app/"
                    size={40}
                    className="mx-auto"
                  />
                  <p className="text-center text-[5px] text-gray-600 mt-1">
                    Scanne für den Belichtungsrechner
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );

    return (
      <div className={`${baseClasses} ${sizeClasses}`}>
        <div className="relative">
          {isZoomed && (
            <div className="fixed inset-0 z-40" onClick={() => setZoomedPage(null)} />
          )}
          {pageContent}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 py-12">
      <div className="max-w-4xl mx-auto px-4">
        <Link
          to="/#demos"
          className="inline-flex items-center text-[#0055AA] hover:text-[#003366] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zu den Demos
        </Link>
        
        <div className="perspective-1000">
          <div className="flex justify-center gap-1">
            <PageContent side="left" />
            <PageContent side="right" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Demo2;